<template>
  <div class="flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Bulk Edit Contacts</span>
    </div>

    <div v-if="isLoading" class="d-flex justify-content-center align-items-center pt-35-px">
      <Spinner />
    </div>

    <transition name="slide-up">
      <div v-if="!isLoading" class="p-20-px flex-grow-1 overflow-y-auto">
        <!-- Contact Selection Table -->
        <a-row>
          <a-col :span="24">
            <div class="section-box">
              <div class="section-header-row">
                <div class="ant-form-item-label">
                  <label>Select Contacts</label>
                </div>
                <a-tag v-if="selectedIds.length > 0" color="blue" class="selected-tag">
                  {{ selectedIds.length }} selected
                </a-tag>
              </div>
              <a-row :gutter="16" class="mt-3">
                <a-col :span="12">
                  <div class="a-input-group" :class="{ active: !!searchTerm }">
                    <a-button size="large">
                      <a-icon type="search" />
                    </a-button>
                    <a-input class="custom-prefix" placeholder="Search by Name or Email" @change="onSearch" />
                  </div>
                </a-col>
                <a-col :span="12">
                  <a-select
                    v-model="filterContactGroupId"
                    allowClear
                    showSearch
                    :filterOption="filterOption"
                    placeholder="Filter by Contact Group"
                    size="large"
                    class="full-w"
                    @change="filter"
                  >
                    <a-select-option v-for="group in contactGroups" :key="group.id" :value="group.id">
                      {{ group.description }}
                    </a-select-option>
                  </a-select>
                </a-col>
              </a-row>
              <div class="table-wrapper mt-2">
                <PaginationTable
                  :pagination="pagin"
                  :columns="BulkContactSelectColumn"
                  :dataSource="filterData"
                  :tableSize="tableSize"
                  :hasSelectRow="true"
                  @changeSelectedRow="onSelectionChange"
                />
              </div>
              <div class="pagination-wrapper mt-3">
                <PaginationBar
                  :pageSize.sync="pagin.pageSize"
                  :total="pagin.total"
                  @change="paginChange"
                />
              </div>
            </div>
          </a-col>
        </a-row>

        <!-- Bulk Edit Fields -->
        <a-row class="mt-4">
          <a-col :span="24">
            <div class="section-box">
              <div class="ant-form-item-label mb-3">
                <label>Fields to Update</label>
              </div>

              <!-- Row 1: Contact Group + Email Subject Prefix -->
              <a-row :gutter="20">
                <a-col :span="12">
                  <a-form-item>
                    <template slot="label">
                      Contact Group
                      <a-tooltip placement="top">
                        <template slot="title">
                          <span>Leave empty to keep the current contact group for each contact.</span>
                        </template>
                        <a-icon type="info-circle" theme="filled" class="ml-1" />
                      </a-tooltip>
                    </template>
                    <a-select
                      v-model="fields.contact_group_id"
                      allowClear
                      showSearch
                      :filterOption="filterOption"
                      placeholder="Keep current"
                      size="large"
                    >
                      <a-select-option v-for="group in contactGroups" :key="group.id" :value="group.id">
                        {{ group.description }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
                <a-col :span="12">
                  <a-form-item>
                    <template slot="label">
                      Email Subject Prefix
                      <a-tooltip placement="top">
                        <template slot="title">
                          <span>The text specified here will be added to the front of the subject in the email alerts. Leave empty to keep current, or check "Clear prefix" to remove it.</span>
                        </template>
                        <a-icon type="info-circle" theme="filled" class="ml-1" />
                      </a-tooltip>
                    </template>
                    <a-input
                      v-model="fields.email_subject_prefix"
                      placeholder="Keep current"
                      size="large"
                      allowClear
                      :disabled="clearEmailSubjectPrefix"
                    />
                    <a-checkbox
                      :checked="clearEmailSubjectPrefix"
                      @change="onToggleClearPrefix"
                      class="mt-1"
                    >
                      Clear prefix for selected contacts
                    </a-checkbox>
                  </a-form-item>
                </a-col>
              </a-row>

              <!-- Row 2: Notification Period + Alert Days -->
              <a-row :gutter="20">
                <a-col :span="6">
                  <a-form-item>
                    <template slot="label">
                      Notification Period (From)
                      <a-tooltip placement="top">
                        <template slot="title">
                          <span>Leave empty to keep current.</span>
                        </template>
                        <a-icon type="info-circle" theme="filled" class="ml-1" />
                      </a-tooltip>
                    </template>
                    <a-select
                      v-model="fields.notif_from"
                      allowClear
                      placeholder="Keep current"
                      size="large"
                    >
                      <a-select-option v-for="opt in timeOptions" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
                <a-col :span="6">
                  <a-form-item>
                    <template slot="label">
                      Notification Period (To)
                      <a-tooltip placement="top">
                        <template slot="title">
                          <span>Leave empty to keep current.</span>
                        </template>
                        <a-icon type="info-circle" theme="filled" class="ml-1" />
                      </a-tooltip>
                    </template>
                    <a-select
                      v-model="fields.notif_to"
                      allowClear
                      placeholder="Keep current"
                      size="large"
                    >
                      <a-select-option v-for="opt in timeOptions" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                      </a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
                <a-col :span="12">
                  <a-form-item>
                    <template slot="label">
                      Alert On Which Days
                      <a-tooltip placement="top">
                        <template slot="title">
                          <span>Select days to override. Leave unchecked to keep current.</span>
                        </template>
                        <a-icon type="info-circle" theme="filled" class="ml-1" />
                      </a-tooltip>
                    </template>
                    <div>
                      <a-checkbox
                        :checked="overrideDays"
                        @change="onToggleOverrideDays"
                        class="mr-3"
                      >
                        Override alert days
                      </a-checkbox>
                      <a-checkbox-group
                        v-if="overrideDays"
                        v-model="fields.notif_day"
                        :options="dayList"
                        class="mt-2"
                      />
                    </div>
                  </a-form-item>
                </a-col>
              </a-row>

              <!-- Row 3: Batch Email + Rebaseline -->
              <a-row :gutter="20">
                <a-col :span="12">
                  <a-form-item>
                    <template slot="label">
                      Batch Email Alerts
                      <a-tooltip placement="top">
                        <template slot="title">
                          <span>Group multiple email alerts every 5 minutes and consolidate as a single email.</span>
                        </template>
                        <a-icon type="info-circle" theme="filled" class="ml-1" />
                      </a-tooltip>
                    </template>
                    <a-select
                      v-model="fields.batch_email"
                      allowClear
                      placeholder="Keep current"
                      size="large"
                    >
                      <a-select-option value="enable">Enable</a-select-option>
                      <a-select-option value="disable">Disable</a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
                <a-col :span="12">
                  <a-form-item>
                    <template slot="label">
                      Allow Rebaseline via Email
                      <a-tooltip placement="top">
                        <template slot="title">
                          <span>Allows contact to rebaseline using a HTTPS link sent in email alert. Enable only if your organization's security policy allows this.</span>
                        </template>
                        <a-icon type="info-circle" theme="filled" class="ml-1" />
                      </a-tooltip>
                    </template>
                    <a-select
                      v-model="fields.enable_email_baseline"
                      allowClear
                      placeholder="Keep current"
                      size="large"
                    >
                      <a-select-option value="enable">Enable</a-select-option>
                      <a-select-option value="disable">Disable</a-select-option>
                    </a-select>
                  </a-form-item>
                </a-col>
              </a-row>
            </div>
          </a-col>
        </a-row>

        <!-- Progress Section -->
        <a-row class="mt-4" v-if="isSaving">
          <a-col :span="24">
            <div class="section-box">
              <div class="ant-form-item-label">
                <label>Update Progress</label>
              </div>
              <a-progress
                :percent="progressPercent"
                :status="updateProgress.failed > 0 ? 'exception' : 'active'"
              />
              <p class="progress-text mt-2 mb-0">
                Updating {{ updateProgress.completed }} of {{ updateProgress.total }} contacts...
                <span v-if="updateProgress.failed > 0" class="text-danger">
                  ({{ updateProgress.failed }} failed)
                </span>
              </p>
            </div>
          </a-col>
        </a-row>
      </div>
    </transition>

    <!-- Footer -->
    <div class="a-footer">
      <a-button key="back" size="large" class="mr-3 slide-down-enter-active" @click="onCancel">
        Cancel
      </a-button>
      <a-button
        key="submit"
        size="large"
        type="primary"
        class="slide-down-enter-active"
        :disabled="!canSave"
        :loading="isSaving"
        @click="onSave"
      >
        Save Changes
      </a-button>
    </div>
  </div>
</template>

<script>
  import bulkEditMixin from '@shared/mixins/bulkEdit.mixin';
  import { BulkContactSelectColumn } from '../columnsTable/BulkContactSelectColumn';
  import ContactService from '../services/contact.service';
  import ContactGroupService from '@modules/ContactGroup/services/contact-group.service';

  const dayList = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  export default {
    name: 'BulkContactEdit',
    mixins: [bulkEditMixin],
    data() {
      return {
        BulkContactSelectColumn,
        dayList,
        contacts: [],
        contactGroups: [],
        filterContactGroupId: undefined,
        overrideDays: false,
        clearEmailSubjectPrefix: false,
        fields: {
          contact_group_id: undefined,
          email_subject_prefix: '',
          notif_from: undefined,
          notif_to: undefined,
          notif_day: [],
          batch_email: undefined,
          enable_email_baseline: undefined
        },
        timeOptions: []
      };
    },
    computed: {
      canSave() {
        return this.selectedIds.length > 0 && this.hasChanges && !this.isSaving;
      },
      hasChanges() {
        return (
          this.fields.contact_group_id != null ||
          (this.fields.email_subject_prefix != null && this.fields.email_subject_prefix !== '') ||
          this.clearEmailSubjectPrefix ||
          this.fields.notif_from != null ||
          this.fields.notif_to != null ||
          (this.overrideDays && this.fields.notif_day.length > 0) ||
          this.fields.batch_email != null ||
          this.fields.enable_email_baseline != null
        );
      }
    },
    mounted() {
      this.buildTimeOptions();
      this.getContactGroupList().then(() => {
        return this.getContactList();
      }).then(() => {
        this.isLoading = false;
      });
    },
    methods: {
      buildTimeOptions() {
        this.timeOptions = [];
        for (let hour = 0; hour <= 24; hour++) {
          let hourPad = '' + hour;
          if (hour < 10) hourPad = '0' + hour;
          for (let minute = 0; minute <= 59; minute += 30) {
            let minutePad = '' + minute;
            if (minute < 10) minutePad = '0' + minute;
            if (hour < 24 || minute === 0) {
              this.timeOptions.push({
                label: hourPad + ':' + minutePad,
                value: hourPad + minutePad
              });
            }
          }
        }
      },
      async getContactList() {
        try {
          const data = await ContactService.getList();
          const groups = this.contactGroups;
          this.contacts = (data || []).map((item, index) => {
            const group = groups.find((g) => g.id === item.contact_group_id);
            return {
              ...item,
              index: index + 1,
              contact_group: group ? group.description : '',
              isChecked: false
            };
          });
          this.filter();
        } catch (error) {
          this.$showMultiErrorMessage(error.messages || []);
        }
      },
      async getContactGroupList() {
        try {
          this.contactGroups = await ContactGroupService.getList();
        } catch {
          this.$showErrorMessage('Failed to load contact groups');
        }
      },
      filter() {
        const term = this.searchTerm.trim().toLowerCase();
        this.filterData = this.contacts.filter((item) => {
          if (term && !(item.name.toLowerCase().includes(term) || (item.email && item.email.toLowerCase().includes(term)))) {
            return false;
          }
          if (this.filterContactGroupId != null && item.contact_group_id !== this.filterContactGroupId) {
            return false;
          }
          return true;
        });
        this.pagin = {
          current: 1,
          pageSize: this.pagin.pageSize,
          total: this.filterData.length
        };
        this.pruneSelection(this.contacts);
      },
      onToggleClearPrefix(e) {
        this.clearEmailSubjectPrefix = e.target.checked;
        if (this.clearEmailSubjectPrefix) {
          this.fields.email_subject_prefix = '';
        }
      },
      onToggleOverrideDays(e) {
        this.overrideDays = e.target.checked;
        if (!this.overrideDays) {
          this.fields.notif_day = [];
        }
      },
      buildPayload(currentContact) {
        const payload = {
          name: currentContact.name,
          email: currentContact.email,
          sms: currentContact.sms,
          description: currentContact.description,
          contact_group_id: currentContact.contact_group_id,
          email_subject_prefix: currentContact.email_subject_prefix,
          notif_from: currentContact.notif_from,
          notif_to: currentContact.notif_to,
          notif_day: currentContact.notif_day,
          notif_from_sms: currentContact.notif_from_sms,
          notif_to_sms: currentContact.notif_to_sms,
          notif_day_sms: currentContact.notif_day_sms,
          batch_email: currentContact.batch_email,
          enable_email_baseline: currentContact.enable_email_baseline
        };

        if (this.fields.contact_group_id != null) {
          payload.contact_group_id = this.fields.contact_group_id;
        }
        if (this.clearEmailSubjectPrefix) {
          payload.email_subject_prefix = '';
        } else if (this.fields.email_subject_prefix != null && this.fields.email_subject_prefix !== '') {
          payload.email_subject_prefix = this.fields.email_subject_prefix;
        }
        if (this.fields.notif_from != null) {
          payload.notif_from = this.fields.notif_from;
        }
        if (this.fields.notif_to != null) {
          payload.notif_to = this.fields.notif_to;
        }
        if (this.overrideDays && this.fields.notif_day.length > 0) {
          payload.notif_day = this.fields.notif_day;
        }
        if (this.fields.batch_email != null) {
          payload.batch_email = this.fields.batch_email === 'enable';
        }
        if (this.fields.enable_email_baseline != null) {
          payload.enable_email_baseline = this.fields.enable_email_baseline === 'enable';
        }

        return payload;
      },
      async onSave() {
        if (!this.canSave) return;

        if ((this.fields.notif_from != null) !== (this.fields.notif_to != null)) {
          this.$showErrorMessage('Please set both Notification Period (From) and (To), or leave both empty');
          return;
        }
        if (this.fields.notif_from != null && this.fields.notif_to != null) {
          if (Number(this.fields.notif_from) > Number(this.fields.notif_to)) {
            this.$showErrorMessage('Notification period "From" must be earlier than "To". Overnight ranges (e.g. 22:00 to 06:00) are not supported.');
            return;
          }
        }

        const confirmed = await this.showBulkSaveConfirm(this.selectedIds.length, 'contact');
        if (!confirmed) return;

        const results = await this.executeBatchUpdates(this.selectedIds, async (id) => {
          const currentContact = this.contacts.find(c => c.id === id);
          if (!currentContact) throw new Error(`Contact ${id} not found in local data`);
          const payload = this.buildPayload(currentContact);
          await ContactService.update(id, payload);
        });

        if (results.failed.length === 0) {
          this.$showSuccessMessage(`Successfully updated ${results.success.length} contacts`);
          this.$router.push('/contact/list');
        } else if (results.success.length > 0) {
          this.$showWarningMessage(
            `Updated ${results.success.length} contacts, but ${results.failed.length} failed`
          );
          await this.getContactList();
        } else {
          this.$showErrorMessage('Failed to update all contacts');
        }
      },
      onCancel() {
        this.$router.push('/contact/list');
      }
    }
  };
</script>

<style lang="scss" scoped>
  .section-box {
    background-color: #f7f7f7;
    padding: 20px;
    border-radius: 4px;

    .ant-form-item-label {
      label::after {
        content: '';
      }
    }
  }

  .section-header-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .selected-tag {
    font-size: 14px;
  }

  .full-w {
    width: 100%;
  }

  .table-wrapper {
    max-height: 400px;
    overflow-y: auto;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    background: #fff;
  }

  .pagination-wrapper {
    background: #fff;
    padding: 12px 16px;
    border: 1px solid #e8e8e8;
    border-top: none;
    border-radius: 0 0 4px 4px;
  }

  .progress-text {
    color: #666;
  }

  .text-danger {
    color: #ff4d4f;
  }
</style>
